import {
  DocumentData,
  collection,
  orderBy,
  query,
  where,
} from "firebase/firestore";
import sortBy from "lodash.sortby";
import { collectionData } from "rxfire/firestore";
import { Observable, combineLatest } from "rxjs";
import { map } from "rxjs/operators";
import { db } from "./firebase";
import { round } from "./round";

export interface ILastResult {
  name: string;
  score: number;
  athleteId?: string;
  gender?: string;
}

export interface IScoreboardRow {
  id: string;
  name: string;
  score: number;
}

export interface IRank {
  rank: number;
}

export interface IScoreboard {
  female: IScoreboardRow[];
  male: IScoreboardRow[];
  lastResult: ILastResult;
}

export interface IScoreboardRawData {
  lastResult: DocumentData;
  female: DocumentData[];
  male: DocumentData[];
}

const FEMALE_GROUP_ID = process.env.REACT_APP_FEMALE_GROUP_ID
  ? process.env.REACT_APP_FEMALE_GROUP_ID
  : "not-set";
const MALE_GROUP_ID = process.env.REACT_APP_MALE_GROUP_ID
  ? process.env.REACT_APP_MALE_GROUP_ID
  : "not-set";
// const FEMALE_GROUP_ID = "59DKGKoJ3RPDmdN2ucEg";
// const MALE_GROUP_ID = "zJiFH8GLKed7i3ztIomi";
const METRIC_ID = process.env.REACT_APP_METRIC_ID
  ? process.env.REACT_APP_METRIC_ID
  : "not-set";
// const METRIC_ID = "relativePeak`Force`";
const ORG_ID = process.env.REACT_APP_ORG_ID
  ? process.env.REACT_APP_ORG_ID
  : "not-set";
// const ORG_ID = "c39V2RbUHXR8dptyrBQTN89ugpl2";
const TEAM_ID = process.env.REACT_APP_TEAM_ID
  ? process.env.REACT_APP_TEAM_ID
  : "not-set";
// const TEAM_ID = "UnI4LkrM9ze6BvINMU9y";
const TEST_TYPE = process.env.REACT_APP_TEST_TYPE
  ? process.env.REACT_APP_TEST_TYPE
  : "not-set";
// const TEST_TYPE = "iwRh0Pzy5xUxz5nqLYij";
const LEADERBOARD_START = process.env.REACT_APP_LEADERBOARD_START
  ? parseInt(process.env.REACT_APP_LEADERBOARD_START)
  : 0;
// const LEADERBOARD_START = 1746447754000;
const LEADERBOARD_END = process.env.REACT_APP_LEADERBOARD_END
  ? parseInt(process.env.REACT_APP_LEADERBOARD_END)
  : 0;

if (process.env.NODE_ENV !== "production") {
  console.log(`Org ID: ${ORG_ID}`);
  console.log(`Team ID: ${TEAM_ID}`);
  console.log(`Test Type: ${TEST_TYPE}`);
  console.log(`Metric ID: ${METRIC_ID}`);
  console.log(`Female Group ID: ${FEMALE_GROUP_ID}`);
  console.log(`Male Group ID: ${MALE_GROUP_ID}`);
  console.log(`Leaderboard Scheduled Start: ${LEADERBOARD_START}`);
  console.log(`Starting in ${((LEADERBOARD_START - Date.now())/1000/60).toFixed(2)} minutes`);
  console.log(`Leaderboard Scheduled End: ${LEADERBOARD_END}`);
  console.log(`Ending in ${((LEADERBOARD_END - Date.now())/1000/60).toFixed(2)} minutes`);
}

const athleteNameProvider = (): Observable<Map<string, string> | null> => {
  return collectionData(
    query(
      collection(db, `orgs/${ORG_ID}/athletes`),
      where("teams", "array-contains", TEAM_ID)
    ),
    {
      idField: "id",
    }
  ).pipe(
    map((athletes) => {
      if (athletes.length === 0) return null;
      const athleteNameLookup = new Map<string, string>();
      athletes.forEach((athlete) => {
        athleteNameLookup.set(athlete.id, athlete.name);
      });
      return athleteNameLookup;
    })
  );
};

const scoreboardDataProvider = (): Observable<IScoreboardRawData | null> => {
  const collectionRef = collection(db, `orgs/${ORG_ID}/tests`);
  const queryRef = query(
    collectionRef,
    where("active", "==", true),
    where("teams", "array-contains", TEAM_ID),
    where("testType", "==", TEST_TYPE),
    where("timestamp", ">=", LEADERBOARD_START),
    orderBy("timestamp", "asc")
  );
  return collectionData(queryRef, { idField: "id" }).pipe(
    map((rows) => {
      if (rows.length === 0) return null;
      const lastResult = rows[rows.length - 1];
      let topMale: DocumentData[] = [];
      let topFemale: DocumentData[] = [];

      for (const test of rows) {
        if (test.groups?.includes(FEMALE_GROUP_ID)) {
          topFemale.push(test);
        } else if (test.groups?.includes(MALE_GROUP_ID)) {
          topMale.push(test);
        }
      }

      const [male, female] = pickTopFive(
        topMale,
        topFemale,
        LEADERBOARD_START,
        LEADERBOARD_END
      );

      return {
        lastResult,
        male,
        female,
      };
    })
  );
};

const pickTopFive = (
  male: DocumentData[],
  female: DocumentData[],
  period_start: number,
  period_end: number
) => {
  const topMale = new Map<string, DocumentData>();
  const topFemale = new Map<string, DocumentData>();
  
  // Filter out entries with NaN values and sort by metric value (highest first)
  const sortedMale = sortBy(
    male.filter(o => !isNaN(o[METRIC_ID])), 
    o => -o[METRIC_ID]
  );
  
  const sortedFemale = sortBy(
    female.filter(o => !isNaN(o[METRIC_ID])),
    o => -o[METRIC_ID]
  );
  
  // Process male tests
  for (const trial of sortedMale) {
    // Skip if we already have a result for this athlete or if outside the time window
    if (topMale.has(trial.athleteId)) {
      continue; 
    }
    
    if (trial.timestamp < period_start || trial.timestamp > period_end) {
      continue;
    }
    
    // Add this athlete's best result
    topMale.set(trial.athleteId, trial);
    
    // Stop once we have 5 unique athletes
    if (topMale.size >= 5) break;
  }
  
  // Process female tests
  for (const trial of sortedFemale) {
    // Skip if we already have a result for this athlete or if outside the time window
    if (topFemale.has(trial.athleteId)) {
      continue;
    }
    
    if (trial.timestamp < period_start || trial.timestamp > period_end) {
      continue;
    }
    
    // Add this athlete's best result
    topFemale.set(trial.athleteId, trial);
    
    // Stop once we have 5 unique athletes
    if (topFemale.size >= 5) break;
  }
  
  // Convert to arrays - but don't pad with placeholders
  let maleResults = Array.from(topMale.values());
  let femaleResults = Array.from(topFemale.values());

  return [maleResults, femaleResults];
};

export const scoreboardProvider = (): Observable<IScoreboard | null> => {
  return combineLatest([scoreboardDataProvider(), athleteNameProvider()]).pipe(
    map((results) => {
      const [data, athleteNameLookup] = results;
      if (!data || !athleteNameLookup) return null;
      return {
        lastResult: {
          id: data.lastResult.id,
          name: athleteNameLookup.get(data.lastResult.athleteId) as string,
          score: round(data.lastResult[METRIC_ID], 2),
          athleteId: data.lastResult.athleteId,
          gender: data.lastResult.groups?.includes(FEMALE_GROUP_ID) ? 'female' : 
                  data.lastResult.groups?.includes(MALE_GROUP_ID) ? 'male' : undefined
        },
        female: data.female.map(
          (row) => ({
            id: row.id,
            name: athleteNameLookup.get(row.athleteId) || "",
            score: round(row[METRIC_ID], 2),
          } as IScoreboardRow)
        ),

        male: data.male.map(
          (row) => ({
            id: row.id,
            name: athleteNameLookup.get(row.athleteId) || "",
            score: round(row[METRIC_ID], 2),
          } as IScoreboardRow)
        ),
      };
    })
  );
};
